import ExcelJS from 'exceljs';

/**
 * Generate Excel file from template data with custom styling
 * @param {Array} data - Report data
 * @param {Object} config - Configuration object with title, columns, exportFileName, companyName, customStyles
 */
// Helper function to flatten columns (extract all leaf columns)
const flattenColumns = (columns) => {
    const result = [];
    columns.forEach(col => {
        if (col.children && col.children.length > 0) {
            result.push(...col.children);
        } else {
            result.push(col);
        }
    });
    return result;
};

// Helper function to check if columns have grouping
const hasGroupedColumns = (columns) => {
    return columns.some(col => col.children && col.children.length > 0);
};

export const generateExcelFromTemplate = async (data, config) => {
    try {
        const styles = config.customStyles || {};
        const currentDate = new Date().toLocaleDateString('bg-BG');

        if (!Array.isArray(data)) {
            throw new Error('Report data must be an array');
        }
        if (data.length === 0) {
            throw new Error('No data available for report');
        }

        // Create a new workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(config.title || 'Report', {
            pageSetup: {
                paperSize: 9, // A4
                orientation: 'landscape',
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 0
            }
        });

        // Flatten columns to get all leaf columns
        const flatColumns = flattenColumns(config.columns);
        const hasGrouping = hasGroupedColumns(config.columns);

        // Initially set minimum column widths
        worksheet.columns = [
            { width: 8 }, // # column
            ...flatColumns.map(() => ({ width: 15 }))
        ];

        let currentRow = 1;

        // Add Title (merged cells)
        const titleRow = worksheet.getRow(currentRow);
        worksheet.mergeCells(currentRow, 1, currentRow, config.columns.length + 1);
        const titleCell = worksheet.getCell(currentRow, 1);
        titleCell.value = config.title;
        titleCell.font = {
            size: parseInt(styles.titleFontSize) || 20,
            bold: true,
            color: { argb: convertHexToArgb(styles.titleColor || '#28a745') }
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        titleRow.height = 30;
        currentRow++;

        // Add Company Name (merged cells)
        const companyRow = worksheet.getRow(currentRow);
        worksheet.mergeCells(currentRow, 1, currentRow, config.columns.length + 1);
        const companyCell = worksheet.getCell(currentRow, 1);
        companyCell.value = config.companyName || '';
        companyCell.font = {
            size: parseInt(styles.subtitleFontSize) || 12,
            color: { argb: convertHexToArgb(styles.subtitleColor || '#555555') }
        };
        companyCell.alignment = { vertical: 'middle', horizontal: 'left' };
        companyRow.height = 20;
        currentRow++;

        // Add empty row
        currentRow++;

        // Add Table Headers
        if (hasGrouping) {
            // Parent header row
            const parentHeaderRow = worksheet.getRow(currentRow);
            let colIndex = 1;
            
            // # column spans 2 rows
            worksheet.mergeCells(currentRow, 1, currentRow + 1, 1);
            const numberCell = parentHeaderRow.getCell(1);
            numberCell.value = '#';
            numberCell.font = {
                size: parseInt(styles.tableFontSize) || 10,
                bold: true,
                color: { argb: convertHexToArgb(styles.tableHeaderTextColor || '#ffffff') }
            };
            numberCell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: convertHexToArgb(styles.tableHeaderBgColor || '#28a745') }
            };
            numberCell.border = {
                top: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                left: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                bottom: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                right: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } }
            };
            numberCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            
            colIndex = 2;
            
            config.columns.forEach(col => {
                if (col.children && col.children.length > 0) {
                    // Group header - merge across children columns
                    const startCol = colIndex;
                    const endCol = colIndex + col.children.length - 1;
                    worksheet.mergeCells(currentRow, startCol, currentRow, endCol);
                    
                    const cell = parentHeaderRow.getCell(startCol);
                    cell.value = col.label;
                    cell.font = {
                        size: parseInt(styles.tableFontSize) || 10,
                        bold: true,
                        color: { argb: convertHexToArgb(styles.tableHeaderTextColor || '#ffffff') }
                    };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: convertHexToArgb(styles.tableHeaderBgColor || '#28a745') }
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                        left: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                        bottom: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                        right: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } }
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    
                    colIndex += col.children.length;
                } else {
                    // Regular column - merge 2 rows
                    worksheet.mergeCells(currentRow, colIndex, currentRow + 1, colIndex);
                    
                    const cell = parentHeaderRow.getCell(colIndex);
                    // Use labelKey to get dynamic label from first data item, or fall back to static label
                    cell.value = col.labelKey && data.length > 0 
                        ? (data[0][col.labelKey] || col.label || col.key)
                        : (col.label || col.key);
                    cell.font = {
                        size: parseInt(styles.tableFontSize) || 10,
                        bold: true,
                        color: { argb: convertHexToArgb(styles.tableHeaderTextColor || '#ffffff') }
                    };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: convertHexToArgb(styles.tableHeaderBgColor || '#28a745') }
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                        left: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                        bottom: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                        right: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } }
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    
                    colIndex++;
                }
            });
            
            parentHeaderRow.height = 20;
            currentRow++;
            
            // Child header row
            const childHeaderRow = worksheet.getRow(currentRow);
            colIndex = 2; // Start after # column
            
            config.columns.forEach(col => {
                if (col.children && col.children.length > 0) {
                    col.children.forEach(childCol => {
                        const cell = childHeaderRow.getCell(colIndex);
                        // Use labelKey to get dynamic label from first data item, or fall back to static label
                        cell.value = childCol.labelKey && data.length > 0 
                            ? (data[0][childCol.labelKey] || childCol.label || childCol.key)
                            : (childCol.label || childCol.key);
                        cell.font = {
                            size: parseInt(styles.tableFontSize) || 10,
                            bold: true,
                            color: { argb: convertHexToArgb(styles.tableHeaderTextColor || '#ffffff') }
                        };
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: convertHexToArgb(styles.tableHeaderBgColor || '#28a745') }
                        };
                        cell.border = {
                            top: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                            left: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                            bottom: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                            right: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } }
                        };
                        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                        colIndex++;
                    });
                } else {
                    // Already merged in parent row, skip
                    colIndex++;
                }
            });
            
            childHeaderRow.height = 20;
            currentRow++;
        } else {
            // Simple single-row headers
            const headerRow = worksheet.getRow(currentRow);
            const headers = ['#', ...flatColumns.map(col => {
                // Use labelKey to get dynamic label from first data item, or fall back to static label
                return col.labelKey && data.length > 0 
                    ? (data[0][col.labelKey] || col.label || col.key)
                    : (col.label || col.key);
            })];
            headers.forEach((header, index) => {
                const cell = headerRow.getCell(index + 1);
                cell.value = header;
                cell.font = {
                    size: parseInt(styles.tableFontSize) || 10,
                    bold: true,
                    color: { argb: convertHexToArgb(styles.tableHeaderTextColor || '#ffffff') }
                };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: convertHexToArgb(styles.tableHeaderBgColor || '#28a745') }
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                    left: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                    bottom: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                    right: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } }
                };
                cell.alignment = { 
                    vertical: 'middle', 
                    horizontal: 'center',
                    wrapText: true 
                };
            });
            headerRow.height = 20;
            currentRow++;
        }

        // Add Data Rows
        data.forEach((item, index) => {
            const dataRow = worksheet.getRow(currentRow);
            const rowData = [
                index + 1,
                ...flatColumns.map(col => {
                    let value;
                    if (col.render) {
                        value = col.render(item);
                    } else {
                        value = (item[col.key] !== undefined && item[col.key] !== null) ? item[col.key] : '-';
                    }
                    return String(value);
                })
            ];

            rowData.forEach((value, colIndex) => {
                const cell = dataRow.getCell(colIndex + 1);
                cell.value = value;
                cell.font = {
                    size: parseInt(styles.tableFontSize) || 9
                };
                
                // Alternate row colors
                const bgColor = (index % 2 === 0) 
                    ? (styles.tableRowEvenBgColor || '#f1f8f4')
                    : (styles.tableRowOddBgColor || '#ffffff');
                
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: convertHexToArgb(bgColor) }
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                    left: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                    bottom: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } },
                    right: { style: 'thin', color: { argb: convertHexToArgb(styles.tableCellBorderColor || '#dee2e6') } }
                };
                // Enable text wrapping and set alignment
                cell.alignment = { 
                    vertical: 'middle', 
                    horizontal: colIndex === 0 ? 'center' : 'left',
                    wrapText: true 
                };
            });
            // Dynamic row height will be set after auto-sizing columns
            dataRow.height = undefined; // Let Excel auto-calculate based on content
            currentRow++;
        });

        // Auto-size columns based on content
        worksheet.columns.forEach((column, colIndex) => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const cellValue = cell.value ? cell.value.toString() : '';
                // Approximate width calculation considering character width
                // Average character width in Excel units (1 unit ≈ 1 character in Calibri 11)
                let cellLength = 0;
                for (let char of cellValue) {
                    // Cyrillic and uppercase letters are wider
                    if (/[А-ЯЁ]/.test(char)) {
                        cellLength += 1.2;
                    } else if (/[A-Z]/.test(char)) {
                        cellLength += 1.1;
                    } else if (/[а-яё]/.test(char)) {
                        cellLength += 1.0;
                    } else if (/[a-z0-9]/.test(char)) {
                        cellLength += 0.9;
                    } else if (/[il\s]/.test(char)) {
                        cellLength += 0.5;
                    } else {
                        cellLength += 1.0;
                    }
                }
                
                if (cellLength > maxLength) {
                    maxLength = cellLength;
                }
            });
            
            // Set column width with some padding
            // First column (index): min 8, max 10
            // Other columns: min 15, max 60
            if (colIndex === 0) {
                column.width = Math.min(Math.max(maxLength + 2, 8), 10);
            } else {
                column.width = Math.min(Math.max(maxLength + 2, 15), 60);
            }
        });

        // Add empty row before footer
        currentRow++;

        // Add Footer (3 columns)
        const footerRow = worksheet.getRow(currentRow);
        
        // Generated date
        const footerCell1 = footerRow.getCell(1);
        footerCell1.value = `Генерирано на: ${currentDate}`;
        footerCell1.font = {
            size: parseInt(styles.footerFontSize) || 8,
            color: { argb: convertHexToArgb(styles.footerColor || '#666666') }
        };

        // Page info (middle)
        const middleCol = Math.floor((config.columns.length + 1) / 2);
        const footerCell2 = footerRow.getCell(middleCol);
        footerCell2.value = `Страница 1`;
        footerCell2.font = {
            size: parseInt(styles.footerFontSize) || 8,
            color: { argb: convertHexToArgb(styles.footerColor || '#666666') }
        };
        footerCell2.alignment = { horizontal: 'center' };

        // Total records
        const footerCell3 = footerRow.getCell(config.columns.length + 1);
        footerCell3.value = `Общо: ${data.length}`;
        footerCell3.font = {
            size: parseInt(styles.footerFontSize) || 8,
            color: { argb: convertHexToArgb(styles.footerColor || '#666666') }
        };
        footerCell3.alignment = { horizontal: 'right' };

        // Generate and download the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const fileName = `${config.exportFileName}_${new Date().toISOString().split('T')[0]}.xlsx`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        return true;
    } catch (error) {
        console.error('Error generating Excel:', error);
        throw error;
    }
};

/**
 * Convert hex color to ARGB format for ExcelJS
 * @param {string} hex - Hex color code (e.g., '#28a745')
 * @returns {string} ARGB color code (e.g., 'FF28a745')
 */
const convertHexToArgb = (hex) => {
    if (!hex) return 'FF000000';
    
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle shorthand hex (e.g., #555)
    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }
    
    // Add alpha channel (FF = fully opaque)
    return 'FF' + hex.toUpperCase();
};

